import { Fragment, useState } from 'react';

import { useCeleryActive } from '@/hooks/useCeleryConsole';
import { ActiveTask } from '@/types/celeryConsole';

import { Loading, PanelCard, PanelError } from '../../../shared';
import { PollInterval } from '../../../types';
import BadgeFilterRow from '../components/BadgeFilterRow';
import CopyableField from '../components/CopyableField';
import NameFilter from '../components/NameFilter';
import QueueBadge, { QUEUE_NAMES } from '../components/QueueBadge';
import TaskCell from '../components/TaskCell';
import { useToggleSet } from '../hooks/useToggleSet';

const DETAIL_COL_SPAN = 5;

const reprArgs = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const ActiveDetailRow = ({ task, colSpan }: { task: ActiveTask; colSpan: number }) => {
  const args = reprArgs(task.task_args);
  const kwargs = reprArgs(task.task_kwargs);
  return (
    <tr className="bg-gray-50">
      <td colSpan={colSpan} className="px-3 py-2">
        <div className="text-xs space-y-1">
          <CopyableField label="args" value={args} className="font-mono" />
          <CopyableField label="kwargs" value={kwargs} className="font-mono" />
        </div>
      </td>
    </tr>
  );
};

const ActivePanel = ({ interval }: { interval: PollInterval }) => {
  const { data, isLoading, error } = useCeleryActive(interval);
  const expanded = useToggleSet<string>();
  const queues = useToggleSet<string>();
  const [nameFilter, setNameFilter] = useState('');

  const toggleExpanded = (id: string | null) => {
    if (id) expanded.toggle(id);
  };

  if (isLoading && !data) return <Loading />;
  if (error) return <PanelError title="Running now" error={error} />;

  // Stable sort by task_id so the active rows hold their position across
  // polls instead of jittering as running_seconds ticks up.
  const all = (data ?? []).slice().sort((a, b) => (a.task_id ?? '').localeCompare(b.task_id ?? ''));
  const trimmed = nameFilter.trim().toLowerCase();
  const tasks = all.filter((task) => {
    if (trimmed && !(task.task_name ?? '').toLowerCase().includes(trimmed)) return false;
    if (queues.values.size > 0 && !queues.values.has(task.queue ?? '')) return false;
    return true;
  });

  return (
    <PanelCard
      title={`Running now — ${tasks.length === all.length ? all.length : `${tasks.length} of ${all.length}`}`}>
      <div className="space-y-2 mb-3">
        <NameFilter
          value={nameFilter}
          onChange={setNameFilter}
          placeholder="Filter by task dot path…"
        />
        <BadgeFilterRow
          label="Queue"
          options={QUEUE_NAMES}
          selected={queues.values}
          onToggle={queues.toggle}
          onClear={queues.clear}
          renderBadge={(queue) => <QueueBadge queue={queue} />}
        />
      </div>

      {all.length === 0 ? (
        <p className="text-sm text-gray-500">No tasks running.</p>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-gray-500">No running tasks match these filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 w-4" />
                <th className="py-2">Task</th>
                <th className="py-2">Queue</th>
                <th className="py-2">Worker</th>
                <th className="py-2 text-right">Running</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task, index) => {
                const id = task.task_id ?? String(index);
                const isOpen = expanded.values.has(id);
                return (
                  <Fragment key={id}>
                    <tr
                      className="border-t align-top cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleExpanded(task.task_id)}>
                      <td className="py-2 text-xs text-gray-400">{isOpen ? '▼' : '▶'}</td>
                      <TaskCell name={task.task_name} id={task.task_id} />
                      <td className="py-2 whitespace-nowrap">
                        <QueueBadge queue={task.queue} />
                      </td>
                      <td className="py-2 whitespace-nowrap">{task.worker}</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {task.running_display ?? '—'}
                      </td>
                    </tr>
                    {isOpen && <ActiveDetailRow task={task} colSpan={DETAIL_COL_SPAN} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PanelCard>
  );
};

export default ActivePanel;
